import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subject, timer } from 'rxjs';

import { select, Store } from '@ngrx/store';
import { BaseComponent } from '@ptg-shared/components';
import { BannerType } from '@ptg-shared/controls/banner/types/banner.model';
import { Breadcrumb } from '@ptg-shared/types/models/breadcrumb.model';
import { filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { DOCUMENT_TEMPLATE } from 'src/app/admin/features/template/constants';
import { Sort } from '@angular/material/sort';
import { ACTION, DEFAULT_PAGE_SIZE, SortType, STATE } from '@ptg-shared/constance';
import { FIRST_PAGE, PageEvent } from '@ptg-shared/controls/pagination';
import { ACTION_COLUMN, Column, Row } from '@ptg-shared/controls/grid';
import { capitalizeFirstLetter, getDateString } from '@ptg-shared/utils/string.util';
import { DocumentsState } from 'src/app/admin/features/file/store/reducers';
import { getDocumentDownloadAction } from 'src/app/admin/features/file/store/actions/documents.action';
import { AdhocReportTemplateListComponent } from '../adhoc-report-template-list/adhoc-report-template-list.component';
import { MatDialog } from '@angular/material/dialog';
import { AdhocReportState } from '@ptg-member/store/reducers/adhoc-report.reducer';
import { showBanner } from '@ptg-shared/utils/common.util';
import { createReportTemplateSelector, getAdhocReportSelector, removeReportAuditTrailSelector, updateReportTemplateSelector } from '@ptg-member/store/selectors/adhoc-report.selector';
import { clearAdhocReportStateAction, clearCreateReportTemplateStateAction, clearRemoveReportAuditTrailStateAction, clearUpdateReportTemplateStateAction, getAdhocReportAction, removeReportAuditTrailAction } from '@ptg-member/store/actions/adhoc-report.actions';
import { AdhocReportStatus } from '@ptg-processing/features/report/constants';
import { ConfirmPopupComponent } from '@ptg-shared/controls/confirm-popup/confirm-popup.component';
import { ConfirmType } from '@ptg-shared/constance/confirm-type.const';

const datePipe = new DatePipe('en-US');

@Component({
  selector: 'ptg-adhoc-report-list',
  templateUrl: './adhoc-report-list.component.html',
  styleUrls: ['./adhoc-report-list.component.scss'],
})
export class AdhocReportListComponent extends BaseComponent implements OnInit {
  readonly BannerType = BannerType;

  listBreadcrumbs: Breadcrumb[] = [
    {
      name: DOCUMENT_TEMPLATE.ADHOC_REPORT,
    },
  ];

  bannerType: BannerType = BannerType.Hidden;
  message = '';

  editForm!: FormGroup;
  formSubmit$ = new Subject<boolean>();
  downloadCompleted$ = new Subject<void>();
  isGenerateLoading: boolean = false;

  currentYear = new Date().getFullYear();

  tableData: any[] = [];
  isTableLoading: boolean = true;
  sortInfo!: Sort;
  defaultPageSize: number = DEFAULT_PAGE_SIZE;
  pageSize: number = 50;
  pageIndex: number = FIRST_PAGE;
  columns: Column[] = [
    {
      name: 'dateCreated',
      header: {
        title: 'Generated at',
      },
      width: '100px',
      sortable: true,
      truncate: true,
    },
    {
      name: 'fileName',
      header: {
        title: 'File Name',
      },
      width: '300px',
      sortable: true,
      truncate: true,
      style: { 'max-width': '30vw' }
    }, 
    {
      name: 'generatedBy',
      header: {
        title: 'Generated by',
      },
      width: '150px',
      sortable: true,
      truncate: true,
    },
    {
      name: 'status',
      header: {
        title: 'Status',
      },
      width: '150px',
      sortable: true,
      truncate: true,
    },
    {
      name: ACTION_COLUMN,
      header: {
        title: 'Action'
      },
      width: '100px',
    }
  ];
  totalRecords: number | any;
  downloadFileName: string = '';

  constructor(
    private datePipe: DatePipe,
    private documentStore: Store<DocumentsState>,
    private adhocReportStore: Store<AdhocReportState>,
    public dialog: MatDialog,
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.getData();
    this.getAdhocReportSelector();
    this.checkStatusUpdateAdhocReport();
    this.checkStatusCreateAdhocReport();
  }

  getAdhocReportSelector() {
    this.adhocReportStore
      .select(getAdhocReportSelector)
      .pipe(
        filter((response) => !!response && !response.isLoading),
        tap((response) => {
          if (this.isTableLoading) {
            this.isTableLoading = !!response?.isLoading;
          }
        }),
        map((response) => response?.payload),
        tap((data) => {
          this.totalRecords = data?.total as number;
          this.tableData = data?.reports.map(item => {
            return {
              ...item,
              dateCreated: this.datePipe.transform(getDateString(item.dateCreated), 'MM/dd/yyyy h:mm a'),
              status: this.getStatusDisplayText(item.status),
              downloadable: item.status === AdhocReportStatus.Completed,
              removable: item.status !== AdhocReportStatus.InProgress
            };
          }) ?? [];
          this.adhocReportStore.dispatch(clearAdhocReportStateAction());
        }),
        switchMap(() => timer(5000)),
        takeUntil(this.unsubscribe$),
      ).subscribe(() => {
        this.getData();
      });
  }

  private getStatusDisplayText(status: AdhocReportStatus): string {
    switch (status) {
      case AdhocReportStatus.InProgress:
        return 'InProgress';
      case AdhocReportStatus.Completed:
        return 'Completed';
      default:
        return 'Failed';
    }
  }

  downloadLetter(row: Row) {
    if (!row) {
      return;
    }
    this.downloadFileName = row?.fileName;
    this.documentStore.dispatch(
      getDocumentDownloadAction({ fileId: row?.attachmentId as string, fileName: row.fileName as string }),
    );
  }

  getData() {
    let sortType = SortType.DESC;
    let sortNames = 'DateCreated';
    if (this.sortInfo?.active && this.sortInfo?.direction) {
      sortNames = capitalizeFirstLetter(this.sortInfo.active);
      sortType = this.sortInfo.direction === 'desc' ? SortType.DESC : SortType.ASC;
    }

    this.adhocReportStore.dispatch(getAdhocReportAction({
      request: {
        sortNames,
        sortType,
        pageNumber: this.pageIndex,
        pageSize: this.pageSize,
      },
    }));
  }

  onChangePage(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageNumber;
    this.isTableLoading = true;
    this.getData();
  }

  onSortChange(event: Sort) {
    this.sortInfo = event;
    this.isTableLoading = true;
    this.getData();
  }

  openColumnSetDialog() {
    const dialogRef = this.dialog.open(AdhocReportTemplateListComponent, {
      panelClass: 'column-list-popup',
      width: '100%',
      disableClose: true,
      autoFocus: false,
      maxHeight: '548px',
      maxWidth: '848px',
      data: {
      },
    });
  }

  checkStatusCreateAdhocReport() {
      this.adhocReportStore
        .pipe(select(createReportTemplateSelector),
          takeUntil(this.unsubscribe$)
        )
        .subscribe((res) => {
          if (res?.state === STATE.SUCCESS) {
            showBanner.call(this, res.state, `Adhoc Report ${res.templateName}`, ACTION.SAVE);
            this.adhocReportStore.dispatch(clearCreateReportTemplateStateAction());
          }
        });
  }

  checkStatusUpdateAdhocReport() {
      this.adhocReportStore
        .pipe(select(updateReportTemplateSelector),
          takeUntil(this.unsubscribe$)
        )
        .subscribe((res) => {
          if (res?.state === STATE.SUCCESS) {
            showBanner.call(this, res.state, `Adhoc Report ${res.templateName}`, ACTION.SAVE);
            this.adhocReportStore.dispatch(clearUpdateReportTemplateStateAction());
          }
        });
  }

  removeReport(row: any) {
      this.bannerType = BannerType.Hidden;
      const dialogRef = this.dialog.open(ConfirmPopupComponent, {
        panelClass: 'confirm-popup',
        autoFocus: false,
        data: {
          text: 'This document will be removed and no longer appear in the system. Are you sure you want to proceed?',
          type: ConfirmType.Destruct,
          title: 'This is a destructive action'
        },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.isTableLoading = true;
          this.adhocReportStore.dispatch(removeReportAuditTrailAction({ request: { reportId: row?.id } }));
          this._removeAuditTrailSelector();
        }
      });
    }

    _removeAuditTrailSelector() {
      this.adhocReportStore
        .pipe(select(removeReportAuditTrailSelector),
          takeUntil(this.unsubscribe$)
        )
        .subscribe((res) => {
          if (res?.state)
            if (res.state === STATE.FAIL) {
              showBanner.call(this, BannerType.Fail, '', '', { customMessage: 'Error occurred while removing. Please try again.'});
            }
          this.adhocReportStore.dispatch(clearRemoveReportAuditTrailStateAction());
        });
    }
}

